import { useCallback, useEffect, useRef, useState } from "react";
import { useEntryState, useEntryScroll } from "@/lib/entryState";
import { useUrlState } from "@/lib/useUrlState";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import HeaderFilterMenu from "@/estimating/components/HeaderFilterMenu";
import { useAuth } from "@/lib/AuthContext";
import PersonPick, { peopleFor } from "@/components/PersonPick";
import { findPerson, shortName, usePeople } from "@/lib/people";
import { formatShortMoney } from "@/lib/formatters";
import { toast } from "sonner";
import { FolderKanban, Loader2, ChevronsUpDown, ChevronsDownUp, ChevronDown, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import { opsApi, money, STATUS_TONE } from "./lib/api";
import ProjectDetail from "./ProjectDetail";
import { takeProjectRequest } from "./tabs/submittals/navigation";
import { eventWords, activityPath } from "./lib/activity";

const ARCHIVED = ["closed", "cancelled"];   // Closed / Cancelled jobs live under Archived
const AGO = (iso) => {
  if (!iso) return "—";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
};

/** Operations > Projects — every won job, with the one thing that matters: what state it is in. */
export default function OperationsProjectsPage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [view, setView] = useUrlState("view", "active");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState({ key: "job", dir: "asc" });
  const [search, setSearch] = useEntryState("q", "");
  const listRef = useRef(null);
  useEntryScroll(listRef);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [who, setWho] = useState({ pm: "", pe: "", foreman: "", gc: "" });   // column filters, "|"-separated emails ("~" = unassigned)
  const [groupBy, setGroupBy] = useState(() =>
    (["pm", "project_engineer", "foreman", "estimator", "account_manager"].includes(user?.role) ? "mine" : ""));
  // Clicking the active pill again drops back to the default view: all jobs, # ascending.
  const pickGroup = (k) => { if (k === groupBy) { setGroupBy(""); setSort({ key: "job", dir: "asc" }); } else setGroupBy(k); };
  const { people } = usePeople();
  const me = (user?.email || "").toLowerCase();
  // The open job (and its first tab) live in the URL so Back / Forward move between list and job.
  const [jobParam, setJobParam] = useUrlState("job", "");
  const [openId, openTab = ""] = jobParam ? jobParam.split(":") : [null];
  const openJob = (id, tab = "") => setJobParam(id ? (tab ? `${id}:${tab}` : id) : "");

  // SubBuilder's "Back to project" lands here — straight back onto the log it came from.
  useEffect(() => {
    const pick = () => {
      const req = takeProjectRequest();
      if (req?.project_id) openJob(req.project_id, req.tab || "");
    };
    pick();
    const jump = (e) => { if (e.detail?.tab === "opsprojects") pick(); };
    window.addEventListener("abs:navigate", jump);
    return () => window.removeEventListener("abs:navigate", jump);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await opsApi.listProjects({ status: search.trim() ? "" : status || viewStatuses.join(",") });
      setRows(d.projects || []);
      setCounts(d.counts || {});
      setStatuses(d.statuses || []);
    } finally {
      setLoading(false);
    }
  }, [status, view, statuses.length, !!search.trim()]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  const inView = (st) => ARCHIVED.includes(st) === (view === "archived");
  const viewStatuses = statuses.filter((x) => inView(x.value)).map((x) => x.value);
  const pickView = (v) => { setView(v); setStatus(""); };
  const FIELD = { pm: "pm_email", pe: "pe_email", foreman: "foreman_email", gc: "customer_name" };
  const nameOf = (email) => shortName(findPerson(people, email)?.name || email);
  const needle = search.trim().toLowerCase();
  const hit = (r) => !needle || [r.name, r.display, r.job_number, r.customer_name, r.city, r.state, r.status_label,
    ...["pm", "pe", "foreman"].flatMap((k) => [r[FIELD[k]], findPerson(people, r[FIELD[k]])?.name])]
    .some((v) => String(v || "").toLowerCase().includes(needle));
  const sorted = rows.filter((r) => hit(r) && inView(r.status) && (groupBy !== "mine" || [r.pm_email, r.pe_email, r.foreman_email, r.am_email, r.estimator_email].some((e) => (e || "").toLowerCase() === me))
    && Object.entries(who).every(([k, v]) => !v || v.split("|").includes((r[FIELD[k]] || "~").toLowerCase())));
  const total = sorted.reduce((t, r) => t + (Number(r.contract_value) || 0), 0);
  const assign = async (r, key, email) => {
    try { await opsApi.patchProject(r.id, { [`ops_${key === "pe" ? "pe" : key === "pm" ? "pm" : "foreman"}_email`]: email }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not saved"); }
  };
  // Column filter: everyone holding the role plus anyone actually assigned on a row, then Unassigned.
  const gcOpts = [...new Set(rows.map((r) => (r.customer_name || "").toLowerCase()).filter(Boolean))]
    .map((v) => ({ value: v, label: rows.find((r) => (r.customer_name || "").toLowerCase() === v).customer_name }))
    .sort((a, b) => a.label.localeCompare(b.label)).concat({ value: "~", label: "No GC" });
  const colOpts = (k) => {
    const emails = new Set([...peopleFor(people, k).map((p) => p.email.toLowerCase()),
                            ...rows.map((r) => (r[FIELD[k]] || "").toLowerCase()).filter(Boolean)]);
    return [...[...emails].map((e) => ({ value: e, label: nameOf(e) })).sort((a, b) => a.label.localeCompare(b.label)),
            { value: "~", label: "Unassigned" }];
  };
  const GROUP_KEY = { status: (r) => r.status_label || "—", pm: (r) => (r.pm_email ? nameOf(r.pm_email) : "Unassigned"),
    pe: (r) => (r.pe_email ? nameOf(r.pe_email) : "Unassigned"), foreman: (r) => (r.foreman_email ? nameOf(r.foreman_email) : "Unassigned"),
    gc: (r) => r.customer_name || "No GC" };
  const [folded, setFolded] = useState([]);   // collapsed group keys
  const groups = (() => {
    if (!GROUP_KEY[groupBy]) return [{ key: "all", label: "", rows: sorted }];
    const m = new Map();
    sorted.forEach((r) => { const k = GROUP_KEY[groupBy](r); m.set(k, [...(m.get(k) || []), r]); });
    const order = groupBy === "status" ? statuses.map((x) => x.label) : [...m.keys()].sort((a, b) => a.localeCompare(b));
    return order.filter((k) => m.has(k)).map((k) => ({ key: k, label: k, rows: m.get(k) }));
  })();
  if (sort.key !== "created") {
    const pick = {
      project: (r) => (r.name || "").toLowerCase(),
      job: (r) => (r.job_number == null ? "~" : String(r.job_number).padStart(8, "0")),
      customer: (r) => (r.customer_name || "").toLowerCase(),
      status: (r) => (r.status_label || "").toLowerCase(),
      contract: (r) => r.contract_value || 0,
      progress: (r) => r.progress?.pct || 0,
      pm: (r) => r.pm_email || "~", pe: (r) => r.pe_email || "~", foreman: (r) => r.foreman_email || "~",
    }[sort.key];
    sorted.sort((a, b) => {
      const x = pick(a), y = pick(b);
      const n = x > y ? 1 : x < y ? -1 : 0;
      return sort.dir === "asc" ? n : -n;
    });
  }

  const sortBy = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const STICKY = { job: "sticky left-8 z-20 w-24 min-w-[6rem]", project: "sticky left-32 z-20 min-w-[16rem]" };
  const STICKY_HEAD = { backgroundColor: "hsl(var(--card))", backgroundImage: "linear-gradient(hsl(var(--muted) / 0.4), hsl(var(--muted) / 0.4))" };
  const Th = ({ label, sortKey, align = "left", children }) => (
    <th className={`whitespace-nowrap px-4 py-2 text-${align} ${STICKY[sortKey] || ""}`} style={STICKY[sortKey] ? STICKY_HEAD : undefined}>
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        <button onClick={() => sortBy(sortKey)} data-testid={`ops-sort-${sortKey}`}
                className={`inline-flex items-center gap-1 hover:text-foreground ${sort.key === sortKey ? "text-foreground" : ""}`}>
          {label}
          {sort.key !== sortKey ? <ChevronsUpDown className="h-3 w-3 opacity-40" />
            : sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
        </button>
        {children}
      </span>
    </th>
  );


  if (openId) {
    return <ProjectDetail key={openId} projectId={openId} initialTab={openTab} onSwitch={(id) => setJobParam(id)} />;
  }

  return (
    <div ref={listRef} className="page-body space-y-5" data-testid="ops-projects-page">
      <div className="flex flex-wrap items-center gap-2">
        <SearchBar value={search} onChange={setSearch} placeholder="Search..." testId="ops-projects-search" />
        <Segmented value={view} onChange={pickView}
                   options={[{ key: "active", label: "Active", testId: "ops-view-active" },
                             { key: "archived", label: "Archived", testId: "ops-view-archived" }]}>
          <span className="mx-0.5 h-4 w-px bg-slate-300" />
          {[["mine", "My Jobs"], ["status", "Status"], ["pm", "PM"], ["pe", "PE"], ["foreman", "Foreman"], ["gc", "GC"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => pickGroup(k)} data-testid={`ops-group-${k}`}
                    className={`inline-flex items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      groupBy === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>))}
        </Segmented>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="font-medium text-slate-400" data-testid="ops-projects-count">{sorted.length} job{sorted.length !== 1 ? "s" : ""}</span>
          <span className="font-semibold text-green-800" data-testid="ops-projects-total">Total: {formatShortMoney(total)}</span>
        </div>
      </div>
      <div>
        {search && (
          <p className="mt-1 text-[11px] text-slate-400" data-testid="ops-search-note">
            Searching every {view === "archived" ? "archived" : "active"} job.
          </p>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading jobs…
          </div>
        ) : (
          <table className="w-full min-w-[1280px] text-sm">
            <thead className="bg-muted/40 text-[11px] font-semibold text-muted-foreground">
              <tr>
                <th className="sticky left-0 z-20 w-8 min-w-[2rem] bg-muted/40 px-1 py-2">
                  {groups.some((g) => g.label) && (
                    <button type="button" data-testid="ops-groups-toggle-all"
                            aria-label={folded.length ? "Expand all groups" : "Collapse all groups"}
                            onClick={() => setFolded(folded.length ? [] : groups.filter((g) => g.label).map((g) => g.key))}
                            className="grid h-6 w-6 place-items-center rounded-md text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-900">
                      {folded.length ? <ChevronsUpDown className="h-3.5 w-3.5" /> : <ChevronsDownUp className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </th>
                <Th label="Job #" sortKey="job" />
                <Th label="Name" sortKey="project" />
                <th className="whitespace-nowrap px-4 py-2 text-left">
                  <HeaderFilterMenu label="GC" testid="ops-gc-filter" multi separator="|" sortOnLabel hideCount searchable options={gcOpts}
                                    value={who.gc} onChange={(v) => setWho((w) => ({ ...w, gc: v || "" }))}
                                    onSort={(dir) => setSort({ key: "customer", dir })} sortActive={sort.key === "customer"} direction={sort.dir} />
                </th>
                <Th label="Contract" sortKey="contract" align="right" />
                <th className="whitespace-nowrap px-4 py-2 text-left">
                  <HeaderFilterMenu label="PM" testid="ops-pm-filter" multi separator="|" sortOnLabel hideCount options={colOpts("pm")}
                                    value={who.pm} onChange={(v) => setWho((w) => ({ ...w, pm: v || "" }))}
                                    onSort={(dir) => setSort({ key: "pm", dir })} sortActive={sort.key === "pm"} direction={sort.dir} />
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-left">
                  <HeaderFilterMenu label="PE" testid="ops-pe-filter" multi separator="|" sortOnLabel hideCount options={colOpts("pe")}
                                    value={who.pe} onChange={(v) => setWho((w) => ({ ...w, pe: v || "" }))}
                                    onSort={(dir) => setSort({ key: "pe", dir })} sortActive={sort.key === "pe"} direction={sort.dir} />
                </th>
                <th className="whitespace-nowrap px-4 py-2 text-left">
                  <HeaderFilterMenu label="Foreman" testid="ops-foreman-filter" multi separator="|" sortOnLabel hideCount options={colOpts("foreman")}
                                    value={who.foreman} onChange={(v) => setWho((w) => ({ ...w, foreman: v || "" }))}
                                    onSort={(dir) => setSort({ key: "foreman", dir })} sortActive={sort.key === "foreman"} direction={sort.dir} />
                </th>
                <Th label="% Complete" sortKey="progress" />
                <th className="whitespace-nowrap px-4 py-2 text-left">
                  <HeaderFilterMenu label="Status" testid="ops-status-filter" multi separator="," sortOnLabel hideCount
                                    options={statuses.filter((x) => inView(x.value)).map((x) => ({ value: x.value, label: `${x.label}${counts[x.value] ? ` · ${counts[x.value]}` : ""}` }))}
                                    value={status} onChange={(v) => setStatus(v || "")}
                                    onSort={(dir) => setSort({ key: "status", dir })} sortActive={sort.key === "status"} direction={sort.dir} />
                </th>
                <th className="px-4 py-2 text-left">Last Activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-14 text-center">
                  <FolderKanban className="mx-auto h-8 w-8 text-slate-300" />
                  <p className="mt-2 text-sm font-medium text-slate-700">
                    No jobs with this status
                  </p>
                </td></tr>
              )}
              {groups.map((g) => [
                g.label && (
                  <tr key={`g-${g.key}`} className="cursor-pointer bg-muted/30 hover:bg-muted/60" data-testid={`ops-group-${g.key}`}
                      onClick={() => setFolded((f) => (f.includes(g.key) ? f.filter((k) => k !== g.key) : [...f, g.key]))}>
                    <td className="sticky left-0 z-10 w-8 bg-muted/30 px-2 py-1.5 text-slate-400">
                      {folded.includes(g.key) ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </td>
                    <td colSpan={11} className="px-4 py-1.5 text-[11px] font-semibold text-muted-foreground">
                      {g.label} <span className="font-normal">· {g.rows.length}</span>
                      <span className="ml-2 font-normal text-green-800">{formatShortMoney(g.rows.reduce((t, r) => t + (Number(r.contract_value) || 0), 0))}</span>
                    </td>
                  </tr>),
                ...(folded.includes(g.key) && g.label ? [] : g.rows).map((r) => (
                <tr key={r.id} onClick={() => openJob(r.id)}
                    data-testid={`ops-project-row-${r.id}`}
                    className="group cursor-pointer transition-colors hover:bg-muted">
                  <td className="sticky left-0 z-10 w-8 bg-card px-1 py-3 group-hover:bg-muted" />
                  <td className="sticky left-8 z-10 w-24 min-w-[6rem] bg-card px-4 py-3 tabular-nums text-slate-600 group-hover:bg-muted" data-testid={`ops-job-number-${r.id}`}>{r.job_number ?? "—"}</td>
                  <td className="sticky left-32 z-10 min-w-[16rem] max-w-[22rem] overflow-hidden bg-card px-4 py-3 group-hover:bg-muted">
                    <div className="flex items-center gap-1.5 font-medium text-slate-900">
                      {r.name || r.display}
                    </div>
                    <div className="text-xs text-slate-500">{[r.city, r.state].filter(Boolean).join(", ") || "No address yet"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.customer_name || "—"}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-900">{money(r.contract_value)}</td>
                  {["pm", "pe", "foreman"].map((k) => (
                    <td key={k} className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <PersonPick value={r[FIELD[k]]} kind={k} valueKey="email" asName testid={`ops-${k}-${r.id}`} onChange={(v) => assign(r, k, v)} />
                    </td>))}
                  <td className="px-4 py-3" data-testid={`ops-progress-${r.id}`}
                      title={`Submittals ${r.progress?.parts?.submitted ? "submitted (10%)" : "not submitted"} · Material ${r.progress?.parts?.material_ordered ? "ordered (30%)" : "not ordered"} · ${r.progress?.parts?.approved_hours || 0} of ${r.progress?.parts?.budget_hours || 0} budgeted hours approved`}>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-[#007BFF] transition-[width]" style={{ width: `${r.progress?.pct || 0}%` }} />
                      </div>
                      <span className="w-8 text-right text-xs tabular-nums text-slate-600">{r.progress?.pct || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span title={r.status_meaning}
                          className={`inline-block min-w-[5.5rem] rounded-full border px-2 py-0.5 text-center text-xs font-medium ${STATUS_TONE[r.status] || ""}`}>
                      {r.status_label}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-xs text-slate-500">
                    {r.last_activity ? (
                      <span role="link" tabIndex={0} data-testid={`ops-activity-link-${r.id}`}
                            title="Open this activity"
                            className="cursor-pointer hover:text-[#007BFF] hover:underline"
                            onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { path: activityPath(r.id, r.last_activity) } })); }}>
                        {eventWords(r.last_activity.event_type)}<span className="text-slate-400"> · {AGO(r.last_activity.timestamp)}</span>
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))])}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
